import {
  PhilatelicItemType,
  type StampFromSet,
  type GumType,
  type StampColor,
  type CatalogIds,
  type Zhihao,
  zhihaoForStampInSet,
} from "@briefmarken_katalog/types";

import { StampBaseModel } from "./StampBaseModel.svelte";
import { validateZhihaoField } from "./validateZhihaoField";
import type { TranslationKey } from "$lib/i18n";

export class StampInSetModel extends StampBaseModel {
  onCreate: (stamp: StampFromSet) => void;
  submitLabel: TranslationKey;
  with_gum = $state<GumType | undefined>(undefined);
  color = $state<StampColor | undefined>({ isMultiColored: true });
  print_run = $state<number | null>(null);
  catalog_ids = $state<CatalogIds>({});
  /** The set's Zhihao — a stamp of a set only varies in its position. */
  parentZhihao = $state<Zhihao | undefined>(undefined);
  /** 1-based slot of this stamp in its set; the Zhihao follows from it. */
  positionInSet = $state<number | undefined>(undefined);
  /** What the stamp came with — used when it is shown outside its set's editor. */
  private storedZhihao = $state<Zhihao | undefined>(undefined);
  /**
   * Derived, never edited: the set is the single source, so a stamp cannot drift out of
   * sync when the set's Zhihao changes or another stamp is inserted.
   */
  zhihao = $derived(
    this.positionInSet === undefined
      ? this.storedZhihao
      : zhihaoForStampInSet(this.parentZhihao, this.positionInSet)
  );

  constructor({ onCreate = (stamp: StampFromSet) => {},
                isReadonly = false,
                initialValue = null,
                defaultTitle,
                parentZhihao = undefined,
                positionInSet = undefined,
                submitLabel = 'editor.addStampToSet' as TranslationKey,
              } :
              {
                onCreate?: (stamp: StampFromSet) => void,
                isReadonly?: boolean,
                initialValue?: StampFromSet | null,
                defaultTitle?: string,
                parentZhihao?: Zhihao,
                /** 1-based slot in the set; drives the derived Zhihao. */
                positionInSet?: number,
                submitLabel?: TranslationKey,
              })
  {
    super();
    this.onCreate = onCreate;
    this.isReadonly = isReadonly;
    this.submitLabel = submitLabel;
    this.parentZhihao = parentZhihao;
    this.positionInSet = positionInSet;

    if (initialValue)
    {
      this.title = defaultTitle ?? initialValue.title;
      this.description = initialValue.description;
      this.face_value_value = initialValue.face_value.value;
      this.face_value_unit = initialValue.face_value.unit;
      this.loadPerforation(initialValue.perforation);
      this.picture = initialValue.pictures?.[0];
      this.with_gum = initialValue.with_gum;
      this.color = initialValue.color;
      this.print_run = initialValue.print_run ?? null;
      this.catalog_ids = initialValue.catalog_ids ?? {};
      this.storedZhihao = initialValue.zhihao;
    } else if (defaultTitle !== undefined) {
      this.title = defaultTitle;
    }
  }

  validate(): [boolean, TranslationKey | ''] {
    return validateZhihaoField(this.zhihao) ?? super.validate();
  }

  onsubmit(close: () => void, ev: Event) {
    ev.preventDefault();

    if (!this.isValid)
    {
      return;
    }

    close();

    this.onCreate({
      title: this.title,
      description: this.description,
      face_value: { value: this.face_value_value, unit: this.face_value_unit! },
      perforation: this.buildPerforation(),
      pictures: this.picture ? [this.picture] : [],
      with_gum: this.with_gum,
      color: this.color,
      print_run: this.print_run ?? undefined,
      catalog_ids: Object.keys(this.catalog_ids).length > 0 ? this.catalog_ids : undefined,
      zhihao: this.zhihao,
      type: PhilatelicItemType.StampFromSet,
    });
  }
}
